import React from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import {
    mdiPlus,
    mdiPencil,
    mdiTrashCan,
    mdiTagMultiple,
    mdiSaleOutline,
    mdiFormatListChecks,
} from '@mdi/js';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import Titulo from '@/components/tipografia/Titulo';
import Descricao from '@/components/tipografia/Descricao';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import Skeleton from '@/components/layout/Skeleton';
import AlertBox from '@/components/layout/AlertBox';
import Modal from '@/components/layout/Modal';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import { useDebounce } from '../chat/useDebounce';
import FormCatalogo from './FormCatalogo';
import Cupons from './Cupons';
import RegrasDesconto from './RegrasDesconto';
import PreviaItens from './PreviaItens';
import { Catalogo } from '@tera/shared/src/types/Catalogo';
import styles from './page.module.css';

const FONTE_LABEL: Record<string, string> = {
    dataset: 'Tabela de dados',
    produto: 'Produtos do CRM',
    integration: 'Integração',
};

const Page = () => {
    const [search, setSearch] = React.useState('');
    const [selecionado, setSelecionado] = React.useState<Catalogo | null>(null);
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
    const [mostrarForm, setMostrarForm] = React.useState(false);
    const [mostrarCupons, setMostrarCupons] = React.useState(false);
    const [mostrarRegras, setMostrarRegras] = React.useState(false);
    const [previaDe, setPreviaDe] = React.useState<Catalogo | null>(null);
    const [aExcluir, setAExcluir] = React.useState<number | null>(null);

    const buscaDebounce = useDebounce(search, 500);

    const query = useQuery(['catalogo:search', buscaDebounce], async () => {
        const res = await axios.get('/api/catalogo', {
            params: { txt: buscaDebounce || undefined, t: 50 },
        });
        return res.data;
    });

    const catalogosBox = (query.data?.result ?? []).map((c: any) => ({
        id: c.id,
        nome: c.nome,
    }));

    const salvarMutation = useMutation(
        async (catalogo: Catalogo) => {
            setFieldErrors({});
            return catalogo.id
                ? axios.put(`/api/catalogo/${catalogo.id}`, catalogo)
                : axios.post('/api/catalogo', catalogo);
        },
        {
            onSuccess: () => {
                toast('Catálogo salvo com sucesso', { type: 'success', position: 'top-center' });
                query.refetch();
                setMostrarForm(false);
                setSelecionado(null);
            },
            onError: (error: any) => {
                setFieldErrors(error.response?.data?.fieldErrors || {});
                toast(error.response?.data?.msg ?? 'Erro ao salvar catálogo', {
                    type: 'error',
                    position: 'top-center',
                    autoClose: 8000,
                });
            },
        },
    );

    const excluirMutation = useMutation(
        async (id: number) => axios.delete(`/api/catalogo/${id}`),
        {
            onSuccess: () => {
                toast('Catálogo excluído', { type: 'success', position: 'top-center' });
                query.refetch();
                setAExcluir(null);
            },
            onError: (error: any) =>
                toast(error.response?.data?.msg ?? 'Erro ao excluir catálogo', {
                    type: 'error',
                    position: 'top-center',
                }),
        },
    );

    const abrirForm = (catalogo: Catalogo | null) => {
        setSelecionado(
            catalogo ?? {
                chave: '',
                nome: '',
                descricao: '',
                moeda: 'BRL',
                ativo: true,
                config: { fonte: 'dataset' } as any,
            },
        );
        setMostrarForm(true);
    };

    const renderConteudo = () => {
        if (query.isLoading)
            return (
                <VBox gap={2}>
                    <Skeleton format="box" />
                    <Skeleton format="box" />
                    <Skeleton format="box" />
                </VBox>
            );

        if (query.error)
            return (
                <AlertBox variant="danger">
                    <p>
                        Erro ao carregar catálogos:{' '}
                        {(query.error as any)?.message || 'Erro desconhecido'}
                    </p>
                </AlertBox>
            );

        const cabecalhos: DataTableHeader[] = [
            { label: 'Nome', width: '25%', column: 'nome' },
            { label: 'Chave', width: '18%', column: 'chave' },
            {
                label: 'Fonte',
                width: '18%',
                render: (l: any) => FONTE_LABEL[l.config?.fonte] ?? '—',
            },
            {
                label: 'Validade',
                width: '12%',
                render: (l: any) => (l.validade_dias ? `${l.validade_dias} dias` : 'sem prazo'),
            },
            { label: 'Ativo', width: '10%', render: (l: any) => (l.ativo ? 'Sim' : 'Não') },
            {
                label: 'Ações',
                width: '160px',
                render: (linha: any) => (
                    <HBox gap={8}>
                        <Botao
                            variant="none-primary"
                            title="Prévia dos itens"
                            icon={mdiFormatListChecks}
                            onClick={() => setPreviaDe(linha)}
                        />
                        <Botao
                            variant="none-primary"
                            title="Editar"
                            icon={mdiPencil}
                            onClick={() => abrirForm(linha)}
                        />
                        <Botao
                            variant="none-danger"
                            title="Excluir"
                            icon={mdiTrashCan}
                            onClick={() => setAExcluir(linha.id)}
                        />
                    </HBox>
                ),
            },
        ];

        return <DataTable cabecalhos={cabecalhos} linhas={query.data?.result ?? []} />;
    };

    return (
        <LayoutUsuario>
            <div className={styles.container}>
                <VBox gap={24}>
                    <HBox>
                        <div className={styles.headerTitulo}>
                            <Titulo>Catálogos de orçamento</Titulo>
                            <Descricao>
                                De onde saem os itens e preços que o chatbot e a IA usam para
                                montar um orçamento. Todo cálculo é feito no servidor: a IA só
                                informa item e quantidade, nunca preço nem desconto.
                            </Descricao>
                        </div>
                        <div className={styles.headerAcoes}>
                            <Botao
                                variant="none-primary"
                                icon={mdiSaleOutline}
                                title="Cupons"
                                onClick={() => setMostrarCupons(true)}
                            >
                                Cupons
                            </Botao>
                            <Botao
                                variant="none-primary"
                                icon={mdiTagMultiple}
                                title="Descontos"
                                onClick={() => setMostrarRegras(true)}
                            >
                                Descontos
                            </Botao>
                            <Botao
                                variant="primary"
                                icon={mdiPlus}
                                title="Novo catálogo"
                                onClick={() => abrirForm(null)}
                            >
                                Novo catálogo
                            </Botao>
                        </div>
                    </HBox>

                    <CampoTexto
                        instanceId="catalogo-search"
                        label=""
                        placeholder="Buscar catálogo..."
                        value={search}
                        onChange={setSearch}
                    />

                    <div>{renderConteudo()}</div>
                </VBox>
            </div>

            <Modal
                instanceId="catalogo-form-modal"
                show={mostrarForm}
                size="lg"
                title={selecionado?.id ? 'Editar catálogo' : 'Novo catálogo'}
                onClose={() => setMostrarForm(false)}
                onCancel={() => setMostrarForm(false)}
                onConfirm={() => selecionado && salvarMutation.mutate(selecionado)}
                confirmProps={{
                    label: 'Salvar',
                    variant: 'primary',
                    disabled: salvarMutation.isLoading,
                }}
                cancelProps={{ label: 'Cancelar' }}
            >
                <FormCatalogo
                    value={selecionado}
                    onChange={setSelecionado}
                    fieldErrors={fieldErrors}
                />
            </Modal>

            <Modal
                instanceId="catalogo-previa-modal"
                show={!!previaDe}
                size="lg"
                title={`Itens · ${previaDe?.nome ?? ''}`}
                onClose={() => setPreviaDe(null)}
                onCancel={() => setPreviaDe(null)}
                cancelProps={{ label: 'Fechar' }}
            >
                {previaDe && <PreviaItens catalogo={previaDe} />}
            </Modal>

            <Modal
                instanceId="catalogo-cupons-modal"
                show={mostrarCupons}
                size="lg"
                title="Cupons promocionais"
                onClose={() => setMostrarCupons(false)}
                onCancel={() => setMostrarCupons(false)}
                cancelProps={{ label: 'Fechar' }}
            >
                <Cupons catalogos={catalogosBox} />
            </Modal>

            <Modal
                instanceId="catalogo-regras-modal"
                show={mostrarRegras}
                size="lg"
                title="Descontos por cliente / marcador"
                onClose={() => setMostrarRegras(false)}
                onCancel={() => setMostrarRegras(false)}
                cancelProps={{ label: 'Fechar' }}
            >
                <RegrasDesconto catalogos={catalogosBox} />
            </Modal>

            <Modal
                instanceId="catalogo-delete-modal"
                show={!!aExcluir}
                size="sm"
                title="Confirmar exclusão"
                onClose={() => setAExcluir(null)}
                onCancel={() => setAExcluir(null)}
                onConfirm={() => aExcluir && excluirMutation.mutate(aExcluir)}
                confirmProps={{
                    label: 'Confirmar',
                    variant: 'danger',
                    disabled: excluirMutation.isLoading,
                }}
                cancelProps={{ label: 'Cancelar' }}
            >
                <p>Tem certeza que deseja excluir este catálogo?</p>
                <p>
                    Agentes de IA, nós de fluxo e formulários que apontam para ele deixarão de
                    conseguir cotar. Orçamentos já emitidos são preservados.
                </p>
            </Modal>
        </LayoutUsuario>
    );
};

export default Page;
